import type {
  AnchorHTMLAttributes,
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ElementType,
  MouseEvent,
  ReactNode,
} from 'react'

import { cn } from '@gto/ui/cn'
import { ArrowUpRightIcon } from '@gto/ui/icons/outline'

import { linkVariants } from './link.styles'

type LinkUnderlineMode = 'always' | 'onHover' | 'none'

type BaseLinkProps = {
  /**
   * Underline mode:
   * - `always` — body-colored text with a resting underline.
   * - `onHover` — brand-colored text, underline appears only on hover/focus.
   * - `none` — no underline; shows the external-style trailing arrow.
   */
  underline?: LinkUnderlineMode
  /** Disables interaction and mutes styles */
  disabled?: boolean
  /** Opens in a new tab with `rel="noopener noreferrer"` */
  external?: boolean
  children?: ReactNode
  className?: string
  'data-tst'?: string
}

type AnchorLinkProps = BaseLinkProps & {
  href: string
  as?: 'a'
  ref?: ComponentPropsWithRef<'a'>['ref']
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseLinkProps | 'href' | 'ref'>

type PolymorphicLinkProps<T extends Exclude<ElementType, 'a'>> = BaseLinkProps & {
  as: T
  ref?: ComponentPropsWithRef<T>['ref']
} & Omit<ComponentPropsWithoutRef<T>, keyof BaseLinkProps | 'as' | 'ref'>

export type LinkProps<T extends ElementType = 'a'> = [T] extends ['a']
  ? AnchorLinkProps
  : PolymorphicLinkProps<Exclude<T, 'a'>>

/** Widened only for internal destructuring (anchor props use `as?: never`, which would make `as` unusable here). */
type LinkPropsDestructured<T extends ElementType> = BaseLinkProps & {
  as?: T
  ref?: ComponentPropsWithRef<T>['ref']
}

/**
 * Design system text link (Tailwind + tailwind-variants).
 *
 * @example
 * <Link href="/docs">Documentation</Link>
 *
 * @example
 * <Link href="https://example.com" underline="none" external>
 *   External resource
 * </Link>
 */
const Link = <T extends ElementType = 'a'>(props: LinkProps<T>) => {
  const {
    as,
    underline = 'always',
    disabled = false,
    external = false,
    children,
    className,
    ref,
    'data-tst': dataTestId,
    ...rest
  } = props as LinkPropsDestructured<T>

  const Component = as ?? ('a' as ElementType)
  const showTrailingIcon = underline === 'none'
  const styles = linkVariants({ underline, disabled })
  const isNativeAnchor = typeof Component === 'string' && Component === 'a'

  let restForDom: typeof rest = rest
  if (disabled) {
    const {
      href: _href,
      to: _to,
      onClick: _onClick,
      onAuxClick: _onAuxClick,
      download: _download,
      ping: _ping,
      ...withoutActivatingProps
    } = rest as typeof rest & {
      href?: unknown
      to?: unknown
      onClick?: unknown
      onAuxClick?: unknown
      download?: unknown
      ping?: unknown
    }
    restForDom = withoutActivatingProps as typeof rest
  }

  const blockActivation = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <Component
      ref={ref}
      aria-disabled={disabled}
      className={cn(styles.root(), showTrailingIcon && !disabled && 'group', className)}
      data-tst={dataTestId}
      data-ui="link"
      tabIndex={disabled ? -1 : undefined}
      {...restForDom}
      {...(!disabled && external ? { rel: 'noopener noreferrer', target: '_blank' } : {})}
      {...(disabled
        ? {
            onClick: blockActivation,
            onAuxClick: blockActivation,
            ...(isNativeAnchor ? { role: 'link' as const } : {}),
          }
        : {})}
    >
      {children}
      {showTrailingIcon ? (
        <ArrowUpRightIcon
          aria-hidden
          className={cn(styles.icon(), !disabled && 'group-focus-visible:hidden')}
        />
      ) : null}
    </Component>
  )
}

export { Link }
